import { isApiError } from '@saay/api-client';
import { Money, StatusBadge } from '@saay/blocks';
import { Ltr } from '../kit.js';
import type { ReactNode } from 'react';

import { marketingLabel, marketingTone } from '../marketing-labels.js';

export function MarketingStatus({ value }: { readonly value: string }) {
  return <StatusBadge tone={marketingTone(value)}>{marketingLabel(value)}</StatusBadge>;
}

/** The large bold figure on a `KpiCard` (counts are not money, so `Money` does not apply). */
export function KpiFigure({ children }: { readonly children: ReactNode }) {
  return <span className="text-2xl font-extrabold tabular-nums">{children}</span>;
}

/**
 * Phone list line: bold title with the amount at the end, then the date or other meta and a
 * status badge. Desktop keeps the table.
 */
export function phoneSummary({
  amountIrr,
  meta,
  status,
  title,
}: {
  readonly amountIrr?: number;
  readonly meta?: ReactNode;
  readonly status?: string;
  readonly title: ReactNode;
}): { readonly primary: ReactNode; readonly secondary: ReactNode } {
  return {
    primary: (
      <span className="flex w-full items-center justify-between gap-2">
        <span className="min-w-0">{title}</span>
        {amountIrr === undefined ? null : <Money rials={amountIrr} />}
      </span>
    ),
    secondary:
      meta === undefined && status === undefined ? (
        ''
      ) : (
        <span className="inline-flex flex-wrap items-center gap-2">
          {meta === undefined ? null : <span>{meta}</span>}
          {status === undefined ? null : <MarketingStatus value={status} />}
        </span>
      ),
  };
}

export function CodeText({ children }: { readonly children: ReactNode }) {
  return <Ltr>{children}</Ltr>;
}

/** Extra recovery line under the server `messageFa` (the message itself is never rewritten). */
export function marketingGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  switch (error.code) {
    case 'NEGATIVE_MARGIN_ACKNOWLEDGEMENT_REQUIRED':
      return 'گزارش اثر را ببینید و تأیید کنید که حاشیه سود ممکن است منفی شود.';
    case 'PREVIEW_EXPIRED':
    case 'PREVIEW_STALE':
      return 'گزارش اثر منقضی شده. یک گزارش تازه بگیرید.';
    case 'COMMISSION_SELF_ASSIGNMENT_FORBIDDEN':
      return 'خودتان را به‌عنوان بازاریاب انتخاب نکنید.';
    case 'PAYOUT_ACCOUNT_UNVERIFIED':
      return 'اول حساب بازاریاب باید تأیید شود.';
    case 'MARKETING_ASSIGNMENT_NOT_EDITABLE':
    case 'MARKETING_STRUCTURE_VERSION_IMMUTABLE':
      return 'این مورد در وضعیت فعلی تغییر نمی‌کند.';
    case 'MARKETING_RECIPIENT_NOT_ACTIVE':
      return 'یک بازاریاب فعال انتخاب کنید.';
    default:
      return undefined;
  }
}
